import React, { useCallback, useEffect, useRef, useState } from 'react';

// Yearly + Seasonal (Spring / Fall) inspections (2026-10-14). Everything comes
// from ERP's Inspection Plan through the portal backend (/inspections); the
// customer requests, reschedules or cancels the appointment of the plan's
// current inspection estimate. ERP is idempotent, so a double click or a retry
// never creates a second estimate, calendar event or confirmation.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WINDOW_LABELS = {
  '8AM-11AM': '8 AM - 11 AM',
  '11AM-2PM': '11 AM - 2 PM',
  '2PM-5PM': '2 PM - 5 PM',
  'Any Time': 'Any time',
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December'];

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function isoDay(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// The date range the request form offers: today .. 12 months, or for a season
// the current/next season window (months are 1-based, from ERP).
export function bookingRange(seasonMonths, now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + 365);
  if (!seasonMonths || !seasonMonths.length) return { min: isoDay(today), max: isoDay(horizon) };
  const first = seasonMonths[0];
  const last = seasonMonths[seasonMonths.length - 1];
  let year = today.getFullYear();
  if (today.getMonth() + 1 > last) year += 1;
  const start = new Date(year, first - 1, 1);
  const end = new Date(year, last, 0); // last day of the last season month
  return { min: isoDay(start > today ? start : today), max: isoDay(end) };
}

function seasonText(months) {
  if (!months || !months.length) return '';
  return `${MONTHS[months[0] - 1]} - ${MONTHS[months[months.length - 1] - 1]}`;
}

function StatusChip({ status }) {
  const s = (status || '').toLowerCase();
  const style = s === 'confirmed' ? 'bg-emerald-400/15 text-emerald-200 border-emerald-300/20'
    : s === 'cancelled' ? 'bg-rose-400/15 text-rose-200 border-rose-300/20'
    : 'bg-amber-400/15 text-amber-100 border-amber-300/20';
  const label = s === 'pending confirmation' ? 'Requested - awaiting confirmation' : status;
  return <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${style}`}>{label}</span>;
}

function RequestForm({ program, plan, serviceLocation, current, onDone, onCancel }) {
  const range = bookingRange(program.season_months);
  const [date, setDate] = useState(current?.scheduled_date && current.scheduled_date >= range.min ? current.scheduled_date : '');
  const [time, setTime] = useState(current?.scheduled_time || '8AM-11AM');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return; // one request at a time; ERP also ignores an identical repeat
    if (!date) { setError('Please choose a date.'); return; }
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/customer/${encodeURIComponent(plan.customer)}/inspections/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program: program.program,
          plan: plan.name || undefined,
          service_location: serviceLocation || undefined,
          preferred_date: date,
          preferred_time: time,
          notes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not send your request. Please try again.');
      onDone(data);
    } catch (err) {
      setError(err.message);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 space-y-3" aria-label={`${program.label} request`}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Preferred date</span>
          <input type="date" value={date} min={range.min} max={range.max} required
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-cyan-400/50 focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Arrival window</span>
          <select value={time} onChange={(e) => setTime(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-cyan-400/50 focus:outline-none">
            {Object.entries(WINDOW_LABELS).map(([v, l]) => <option key={v} value={v} className="bg-[#0b1e30]">{l}</option>)}
          </select>
        </label>
      </div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000}
        placeholder="Gate code, pets, anything we should know (optional)"
        className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-400/50 focus:outline-none resize-none" />
      {error && <p role="alert" className="text-sm text-rose-300 bg-rose-400/10 rounded-xl px-3 py-2">{error}</p>}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button type="submit" disabled={busy}
          className="flex-1 rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] py-2.5 text-sm font-bold tracking-wide text-white shadow-lg disabled:opacity-60">
          {busy ? 'Sending...' : current?.scheduled_date ? 'Request new time' : 'Request inspection'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={busy}
            className="rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-slate-200">Back</button>
        )}
      </div>
      <p className="text-xs text-slate-400">We'll confirm your appointment within 1 business day. Nothing is charged when you request.</p>
    </form>
  );
}

function History({ plan }) {
  const [open, setOpen] = useState(false);
  const items = plan?.history || [];
  if (!items.length) return null;
  return (
    <div className="mt-3">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="text-xs font-semibold uppercase tracking-widest text-cyan-200/80 hover:text-cyan-100">
        {open ? 'Hide history' : `Inspection history (${items.length})`}
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5">
          {items.map((h, i) => (
            <li key={i} className="text-xs leading-5 text-slate-300">
              <span className="text-slate-400">{fmtDate(h.occurred_at)}</span> - {h.summary || h.event_type}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProgramCard({ program, plan, customerId, serviceLocation, legacy, onChanged }) {
  const [mode, setMode] = useState('view'); // 'view' | 'request'
  const [flash, setFlash] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const appt = plan?.current_appointment;
  const active = appt && appt.scheduled_date && appt.appointment_status !== 'Cancelled';
  const optedOut = plan && plan.status !== 'Enrolled';

  const cancel = async () => {
    if (cancelling || !window.confirm(`Cancel your ${program.label.toLowerCase()} on ${fmtDate(appt.scheduled_date)}?`)) return;
    setCancelling(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/customer/${encodeURIComponent(customerId)}/inspection-plans/${encodeURIComponent(plan.name)}/cancel-appointment`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not cancel. Please call (469) 751-3567.');
      setFlash('Your appointment was cancelled.');
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="rounded-[22px] border border-white/10 bg-white/5 p-4" data-program={program.program}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-base font-semibold text-white">{program.label}</p>
        {program.season_months?.length > 0 && (
          <span className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-2.5 py-0.5 text-[11px] font-semibold text-cyan-100">
            {seasonText(program.season_months)}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm leading-6 text-slate-300">{program.description}</p>

      {plan?.next_due_date && plan.status === 'Enrolled' && !active && (
        <p className="mt-2 text-sm text-slate-200">Next inspection due: <strong className="text-white">{fmtDate(plan.next_due_date)}</strong></p>
      )}
      {plan?.last_completed_on && (
        <p className="mt-1 text-xs text-slate-400">Last completed {fmtDate(plan.last_completed_on)}</p>
      )}
      {!plan && legacy && (
        <p className="mt-2 text-sm text-emerald-200">You opted in to yearly inspections. Choose a date below to schedule your visit.</p>
      )}

      {active && mode === 'view' && (
        <div className="mt-3 rounded-2xl border border-emerald-300/20 bg-emerald-400/10 p-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-200/80">Your appointment</p>
          <p className="mt-1 text-sm font-bold text-white">
            {fmtDate(appt.scheduled_date)} · {WINDOW_LABELS[appt.scheduled_time] || appt.scheduled_time || 'Any time'}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <StatusChip status={appt.appointment_status || 'Pending Confirmation'} />
            {appt.technician && <span className="text-xs text-slate-300">Technician: {appt.technician}</span>}
          </div>
          {appt.locked ? (
            <p className="mt-2 text-xs text-slate-300">To change this visit, call us at (469) 751-3567.</p>
          ) : (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <button type="button" onClick={() => { setFlash(''); setMode('request'); }}
                className="flex-1 rounded-xl border border-white/15 bg-white/10 py-2 text-sm font-semibold text-white">Reschedule</button>
              <button type="button" onClick={cancel} disabled={cancelling}
                className="flex-1 rounded-xl border border-rose-300/30 py-2 text-sm font-semibold text-rose-200 disabled:opacity-60">
                {cancelling ? 'Cancelling...' : 'Cancel'}
              </button>
            </div>
          )}
        </div>
      )}

      {flash && <p role="status" className="mt-3 text-sm text-emerald-200">{flash}</p>}
      {error && <p role="alert" className="mt-3 text-sm text-rose-300 bg-rose-400/10 rounded-xl px-3 py-2">{error}</p>}

      {optedOut ? (
        <p className="mt-3 text-xs text-slate-400">This program is {plan.status.toLowerCase()} for this property. Call (469) 751-3567 to restart it.</p>
      ) : mode === 'request' ? (
        <RequestForm program={program} plan={{ ...(plan || {}), customer: customerId }} serviceLocation={serviceLocation}
          current={appt} onCancel={() => setMode('view')}
          onDone={(r) => {
            setMode('view');
            setFlash(r.changed === false ? 'You already have this time requested.'
              : r.rescheduled ? "Your new time was requested. We'll confirm it shortly."
              : "Request received! We'll confirm your appointment within 1 business day.");
            onChanged();
          }} />
      ) : !active ? (
        <button type="button" onClick={() => { setFlash(''); setMode('request'); }}
          className="mt-3 w-full rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] py-2.5 text-sm font-bold tracking-wide text-white shadow-lg">
          {program.season_months?.length ? `Schedule ${program.label}` : 'Schedule My Yearly Inspection'}
        </button>
      ) : null}
      <History plan={plan} />
    </div>
  );
}

export default function InspectionsCard({ customerId, serviceLocation, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/customer/${encodeURIComponent(customerId)}/inspections`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || 'unavailable');
      setData(body);
      setError('');
    } catch {
      setError('Inspection details are unavailable right now. Please refresh or call (469) 751-3567.');
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  if (error && !data) {
    return <div className="rounded-[22px] border border-white/10 bg-white/5 p-4 text-sm text-slate-300">{error}</div>;
  }
  if (!data) {
    return <div className="rounded-[22px] border border-white/10 bg-white/5 p-4 text-sm text-slate-400">Loading inspections...</div>;
  }
  const loc = serviceLocation || null;
  const planFor = (program) => (data.plans || []).find((p) => p.program === program && (p.service_location || null) === loc);
  const yearly = (data.programs || []).filter((p) => !p.season_months?.length);
  const seasonal = (data.programs || []).filter((p) => p.season_months?.length);
  const card = (p) => (
    <ProgramCard key={`${p.program}-${loc || ''}`} program={p} plan={planFor(p.program)} customerId={customerId}
      serviceLocation={loc} legacy={p.program === 'Yearly' ? data.legacy_opt_in : null}
      onChanged={() => { load(); if (onChanged) onChanged(); }} />
  );
  return (
    <div className="space-y-4" data-testid="inspections">
      {yearly.map(card)}
      {seasonal.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-200/70">Seasonal inspections</p>
          {seasonal.map(card)}
        </div>
      )}
    </div>
  );
}
